/**
 * Auth helpers for the frontend.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.druploy.dev';

export interface OrgMembership {
  org_id: number;
  org_slug: string;
  org_name: string;
  role: string; // owner | admin | member | viewer
  color: string;
  gitlab_url: string | null;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  avatar_url: string | null;
  is_superadmin: boolean;
  system_role: string | null;
  organizations: OrgMembership[];
  created_at: string;
  updated_at: string;
}

export interface APITokenInfo {
  id: number;
  name: string;
  token_prefix: string;
  created_at: string;
  last_used_at: string | null;
}

export async function fetchMe(): Promise<AuthUser | null> {
  try {
    const resp = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
    if (!resp.ok) return null;
    return resp.json();
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  await fetch(`${API_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

// ---- User API Tokens ----

export async function fetchTokens(): Promise<APITokenInfo[]> {
  const resp = await fetch(`${API_URL}/api/auth/tokens`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch tokens');
  const data = await resp.json();
  return data.tokens;
}

export async function createToken(name: string): Promise<{ id: number; token: string }> {
  const resp = await fetch(`${API_URL}/api/auth/tokens`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!resp.ok) throw new Error('Failed to create token');
  return resp.json();
}

export async function revokeToken(id: number): Promise<void> {
  const resp = await fetch(`${API_URL}/api/auth/tokens/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to revoke token');
}

// ---- Org Members ----

export interface OrgMember {
  id: number;
  email: string;
  name: string;
  avatar_url: string | null;
  role: string;
  joined_at: string;
}

export async function fetchOrgMembers(orgSlug: string): Promise<OrgMember[]> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/members`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch members');
  const data = await resp.json();
  return data.members;
}

export async function updateMemberRole(orgSlug: string, userId: number, role: string): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/members/${userId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!resp.ok) throw new Error('Failed to update role');
}

export async function removeMember(orgSlug: string, userId: number): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/members/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to remove member');
}

// ---- CLI ----

export async function approveCLI(code: string): Promise<void> {
  const resp = await fetch(`${API_URL}/api/auth/cli/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!resp.ok) throw new Error('Failed to approve CLI request');
}

export function getLoginUrl(provider: 'gitlab' | 'google'): string {
  return `${API_URL}/api/auth/login/${provider}`;
}

// ---- Org Invitations ----

export interface Invitation {
  id: number;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
  invited_by_name?: string;
}

export async function inviteUser(orgSlug: string, email: string, role: string): Promise<Invitation> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/invitations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to send invitation');
  }
  return resp.json();
}

export async function fetchInvitations(orgSlug: string): Promise<Invitation[]> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/invitations`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch invitations');
  const data = await resp.json();
  return data.invitations;
}

export async function cancelInvitation(orgSlug: string, id: number): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/invitations/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to cancel invitation');
}

export async function validateInvitation(token: string): Promise<{ email: string; role: string; org_name: string }> {
  const resp = await fetch(`${API_URL}/api/auth/invitations/validate?token=${encodeURIComponent(token)}`);
  if (!resp.ok) throw new Error('Invalid or expired invitation');
  return resp.json();
}

export async function acceptInvitation(token: string, name: string): Promise<void> {
  const resp = await fetch(`${API_URL}/api/auth/invitations/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, name }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to accept invitation');
  }
}

// ---- Project Members ----

export interface ProjectMember {
  id: number;
  email: string;
  name: string;
  avatar_url: string | null;
  role: string;
  created_at: string;
}

export interface ProjectMembersResponse {
  org_members: OrgMember[];
  project_members: ProjectMember[];
  invitations: Invitation[];
}

export async function fetchMyProjectRole(orgSlug: string, projectSlug: string): Promise<string | null> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/projects/${projectSlug}/my-role`, { credentials: 'include' });
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.role;
}

export async function fetchProjectMembers(orgSlug: string, projectSlug: string): Promise<ProjectMembersResponse> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/projects/${projectSlug}/members`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch project members');
  return resp.json();
}

export async function addProjectMember(orgSlug: string, projectSlug: string, email: string, role: string): Promise<any> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/projects/${projectSlug}/members`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to add member');
  }
  return resp.json();
}

export async function updateProjectMemberRole(orgSlug: string, projectSlug: string, userId: number, role: string): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/projects/${projectSlug}/members/${userId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!resp.ok) throw new Error('Failed to update role');
}

export async function removeProjectMember(orgSlug: string, projectSlug: string, userId: number): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/projects/${projectSlug}/members/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to remove member');
}

// ---- Organizations ----

export interface Organization {
  id: number;
  slug: string;
  name: string;
  gitlab_url: string | null;
  auto_erase_enabled: boolean;
  auto_erase_days: number;
  created_at: string;
}

export async function fetchOrganizations(): Promise<Organization[]> {
  const resp = await fetch(`${API_URL}/api/orgs`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch organizations');
  const data = await resp.json();
  return data.organizations;
}

export async function createOrganization(slug: string, name: string, color?: string): Promise<Organization> {
  const resp = await fetch(`${API_URL}/api/orgs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, name, ...(color && { color }) }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to create organization');
  }
  return resp.json();
}

export async function getOrganization(slug: string): Promise<Organization> {
  const resp = await fetch(`${API_URL}/api/orgs/${slug}`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to get organization');
  return resp.json();
}

// ---- Email Domains ----

export interface EmailDomain {
  id: number;
  domain: string;
}

export async function fetchEmailDomains(orgSlug: string): Promise<EmailDomain[]> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/email-domains`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Failed to fetch email domains');
  const data = await resp.json();
  return (data.domains || []).map((d: any) =>
    typeof d === 'string' ? { id: 0, domain: d } : { id: d.id, domain: d.domain }
  );
}

export async function addEmailDomain(orgSlug: string, domain: string): Promise<EmailDomain> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/email-domains`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to add email domain');
  }
  const data = await resp.json();
  return { id: data.id, domain: data.domain };
}

export async function removeEmailDomain(orgSlug: string, domainId: number): Promise<void> {
  const resp = await fetch(`${API_URL}/api/orgs/${orgSlug}/email-domains/${domainId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error('Failed to remove email domain');
}
